import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

type ConfirmDialogProps = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirming?: boolean;
  tone?: 'danger' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * In-app confirm overlay — Escape / backdrop / Cancel dismisses.
 * Prefer this over window.confirm so the prompt matches the dashboard chrome.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirming = false,
  tone = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirming) onCancel();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onCancel, confirming]);

  const isDanger = tone === 'danger';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-desc"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'color-mix(in srgb, #0a0a0b 55%, transparent)' }}
      onClick={() => {
        if (!confirming) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-[14px] border border-line bg-panel p-5 shadow-lg sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" className="m-0 text-lg font-bold text-ink">
          {title}
        </h2>
        <p id="confirm-dialog-desc" className="m-0 mt-2 text-sm leading-relaxed text-muted">
          {description}
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2.5">
          <button
            type="button"
            disabled={confirming}
            onClick={onCancel}
            className="rounded-[10px] border border-line px-4 py-2.5 text-sm font-semibold text-ink hover:border-[var(--gs-accent)] disabled:opacity-50"
          >
            {cancelLabel ?? t('actions.cancel')}
          </button>
          <button
            type="button"
            disabled={confirming}
            onClick={onConfirm}
            className="rounded-[10px] px-4 py-2.5 text-sm font-bold disabled:opacity-50"
            style={
              isDanger
                ? {
                    border: '1px solid var(--gs-bad)',
                    color: 'var(--gs-bad)',
                    background: 'transparent',
                  }
                : {
                    background: 'var(--gs-accent-solid)',
                    color: 'var(--gs-accent-ink)',
                  }
            }
          >
            {confirming ? t('actions.working') : (confirmLabel ?? t('actions.confirm'))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
